import { useQuery } from '@tanstack/react-query';

import { api, ApiError } from '@shared/api/client';
import { asRows } from '@shared/api/envelope';
import { key, STALE_TIME } from '@shared/api/query-keys';

import { CREDIT_ROLES, CREDIT_ROLES_KEY } from './catalog-settings';

import type {
  ArtworkPurposePolicy,
  ContentItemDetail,
  CreditProfileRow,
  CreditRelation,
  ExternalBinding,
  Group,
  TaxonomyTerm,
  UploadPolicy,
} from './types';

/**
 * The reads more than one content surface performs.
 *
 * **Every vocabulary a route publishes is fetched, not compiled in.** The
 * accepted formats, the artwork purposes and their ratios, and the resolution
 * ladder all arrive from `GET /uploadPolicy`, which exists precisely so a format
 * list or a ladder rung is never a literal in a frontend. The settings whose
 * only route an operator cannot read are seeded from the catalog instead — see
 * `catalog-settings.ts`.
 *
 * `staleTime` is the vocabulary default on those: a setting changes about once
 * a quarter, and re-reading it per navigation would spend a request on an
 * answer that has not moved. The title detail takes the detail default of `0`,
 * because a decision taken elsewhere in the console must be visible the moment
 * the operator arrives.
 */

// ---- the title itself ------------------------------------------------------

export function adminItemKey(contentItemId: string) {
  return key('content', 'adminItem', contentItemId);
}

/**
 * The admin read, as the owning service composes it.
 *
 * The record is nested under `item` with the taxonomy, the seasons, the credits
 * and the provider block beside it — not flattened into one object. The console
 * read it as though it were flat, so `state`, `groups` and `metadata` were all
 * `undefined`, and the assets page died on `TypeError: ... .find is not a
 * function` with every request answering 200.
 */
interface WireAdminContentItem {
  item: {
    id: string;
    title: string;
    description: string;
    content_type: string;
    publish_state: string;
    cover_artwork_url: string | null;
    /** A **JSON string**, not an object: it is a `JSONB` column serialised as it is stored. */
    metadata: string | Record<string, unknown> | null;
    published_at: string | null;
    updated_at: string;
    /** Whether at least one asset on the title has finished converting. */
    has_playable_asset?: boolean;
  };
  categoryIds: string[];
  tagIds: string[];
  groups: Group[];
  /**
   * The record half of every asset — kind, title, ordinal, season.
   *
   * `assets` lists the byte half and holds no ordinal by design, so this is the
   * only read that answers what an asset *is*. Without it the assets page drew an
   * empty section over a title that already had a film file, and the operator's
   * next upload was refused as a conflict against a row nothing had shown them.
   */
  mediaAssets: WireMediaAsset[];
  /**
   * **One row per (person, role), not one row per person.**
   *
   * The service returns the credit records exactly as they are stored — a person
   * credited twice on the same title arrives twice — and this console groups them.
   * The shape is not a choice either side made independently: it is the same
   * projection the public detail composition serves, so grouping here rather than
   * asking the service to reshape keeps one query behind both readings.
   */
  credits: WireCredit[];
  externalBinding: ExternalBinding | null;
  externalRatings: unknown[];
}

/** One stored credit record, as `content` serialises it. */
interface WireCredit {
  id: string;
  role: string;
  position: number;
  credit_profile_id: string;
  slug: string;
  display_name: string;
  portrait_url: string | null;
}

interface WireMediaAsset {
  id: string;
  kind: string;
  title: string;
  description: string | null;
  position: number;
  group_id: string | null;
  duration_seconds: number | null;
  max_rendition_height: number | null;
  asset_ready: boolean;
}

export function useAdminContentItem(contentItemId: string | undefined) {
  return useQuery({
    queryKey: adminItemKey(contentItemId ?? ''),
    queryFn: async (): Promise<ContentItemDetail> =>
      normalizeAdminContentItem(
        await api.get<WireAdminContentItem>('content', `/admin/contentItems/${contentItemId}`),
      ),
    enabled: Boolean(contentItemId),
    staleTime: STALE_TIME.detail,
  });
}

/**
 * Group the stored credit records into one row per credited person.
 *
 * **The console read the wire rows as if they were already grouped**, and every
 * title carrying at least one credit crashed the whole edit page on
 * `credit.roles.some(...)` — `roles` was `undefined`, the error surfaced as
 * «تعذّر إتمام الطلب» over the entire route, and the operator had no way to reach
 * the authoring form of any title a provider fill had touched.
 *
 * A credit is one `(person, title, role)` record and the route that corrects one
 * addresses it **by its own id**, so the role list has to carry those ids rather
 * than bare strings: a chip that toggles a role is adding or removing a record.
 *
 * First appearance fixes a person's position, and the role order inside a person
 * is the wire's own — the service already orders by `position`, and re-sorting
 * here would put the same list in two orders on two surfaces of one console.
 */
function groupCredits(rows: readonly WireCredit[] | undefined): CreditRelation[] {
  const grouped = new Map<string, CreditRelation>();

  for (const row of rows ?? []) {
    const held = grouped.get(row.credit_profile_id);
    if (held) {
      held.roles.push({ role: row.role, credit_id: row.id });
      continue;
    }
    grouped.set(row.credit_profile_id, {
      credit_profile_id: row.credit_profile_id,
      display_name: row.display_name,
      roles: [{ role: row.role, credit_id: row.id }],
    });
  }

  return [...grouped.values()];
}

/**
 * Flatten the composed read into the shape these surfaces have always read.
 *
 * Normalised once, here, for the same reason the upload policy is: the alternative
 * is teaching four surfaces the wire's nesting, and the fourth is the one that
 * would be missed.
 *
 * **`metadata` arrives as a JSON string.** It is a `JSONB` column handed back as
 * stored, so it is parsed rather than spread — spreading a string yields an object
 * of numbered characters, which reads as «the year is missing» on every field.
 */
function normalizeAdminContentItem(wire: WireAdminContentItem): ContentItemDetail {
  const item = wire.item;
  const metadata = parseMetadata(item.metadata);

  return {
    id: item.id,
    reference: item.id,
    title: item.title,
    description: item.description ?? '',
    content_type: item.content_type,
    state: item.publish_state,
    has_playable_asset: Boolean(item.has_playable_asset),
    category_id: wire.categoryIds?.[0] ?? null,
    tag_ids: wire.tagIds ?? [],
    metadata: {
      production_year: numberOrNull(metadata.production_year),
      producing_country: stringOrNull(metadata.producing_country),
      age_rating: stringOrNull(metadata.age_rating),
    },
    // The service publishes one cover URL and no geometry beside it. The two
    // dimensions stay `null` rather than being guessed: a fabricated size would
    // be read as the stored one by the purpose table.
    cover: { url: item.cover_artwork_url, width: null, height: null },
    // Composed by `assets`, not by `content`. The surfaces that need it read it
    // from the upload policy; an empty list here is «not part of this payload».
    artwork_purposes: [],
    credits: groupCredits(wire.credits),
    groups: wire.groups ?? [],
    // The byte half — the free marking and the two states — is `assets`' and is
    // joined on by the surface that reads it. Defaulted rather than optional so a
    // consumer never has to ask whether the join has happened yet.
    media_assets: (wire.mediaAssets ?? []).map((row) => ({
      id: row.id,
      kind: row.kind,
      title: row.title,
      description: row.description ?? null,
      position: row.position,
      group_id: row.group_id ?? null,
      duration_seconds: row.duration_seconds ?? null,
      max_rendition_height: row.max_rendition_height ?? null,
      asset_ready: Boolean(row.asset_ready),
      is_free_preview: false,
      storage_state: null,
      encode_state: null,
    })),
    external: wire.externalBinding ?? null,
    published_at: item.published_at,
    updated_at: item.updated_at,
    updated_by_name: null,
  };
}

function parseMetadata(value: string | Record<string, unknown> | null): Record<string, unknown> {
  if (value === null || value === undefined) return {};
  if (typeof value !== 'string') return value;
  try {
    const parsed: unknown = JSON.parse(value);
    return typeof parsed === 'object' && parsed !== null ? (parsed as Record<string, unknown>) : {};
  } catch {
    // A column that did not parse is a column with nothing readable in it. The
    // form draws empty fields, which is true, rather than throwing on a detail read.
    return {};
  }
}

function numberOrNull(value: unknown): number | null {
  return typeof value === 'number' ? value : null;
}

function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

// ---- vocabularies ----------------------------------------------------------

export function categoriesKey() {
  return key('content', 'categories');
}

export function useCategories() {
  return useQuery({
    queryKey: categoriesKey(),
    // Both vocabularies answer `data` as a bare array: neither carries
    // pagination, so neither carries an `items` wrapper.
    queryFn: async () => ({ items: asRows<TaxonomyTerm>(await api.get('content', '/categories')) }),
    staleTime: STALE_TIME.vocabulary,
  });
}

export function tagsKey() {
  return key('content', 'tags');
}

export function useTags() {
  return useQuery({
    queryKey: tagsKey(),
    queryFn: async () => ({ items: asRows<TaxonomyTerm>(await api.get('content', '/tags')) }),
    staleTime: STALE_TIME.vocabulary,
  });
}

export function creditProfilesKey() {
  return key('content', 'creditProfiles');
}

export function useCreditProfiles() {
  return useQuery({
    queryKey: creditProfilesKey(),
    // A bare array, not a page: the route serves a picker's option set rather
    // than a traversable register.
    queryFn: () => api.get<CreditProfileRow[]>('content', '/admin/creditProfiles'),
    staleTime: STALE_TIME.vocabulary,
  });
}

/**
 * **One configuration read on these surfaces, and it is the credit vocabulary.**
 *
 * `GET /api/v1/admin/configurations` is `admin`-gated on every service while
 * three of the four content routes admit an `operator`, so a fetch here `403`s
 * for exactly the role the surface is built for. That is why every other setting
 * these surfaces need is seeded from the catalog in `catalog-settings.ts`.
 *
 * The credit roles are the exception, because they are the one seeded value the
 * **service validates a write against**: `POST …/credits` refuses a role outside
 * its own `CREDIT_ROLES` row with `422 CONTENT_CREDIT_ROLE_NOT_CONFIGURED`. A
 * seeded copy that drifts from that row does not degrade a label — it makes the
 * write impossible, which is exactly what shipped: this console held the six
 * tokens upper-cased and **no credit could be added from a title at all**.
 *
 * So the vocabulary is read, and the seeded list is the fallback an `operator`
 * falls back to on the `403` — the same shape the credited-person surface has
 * used since it was written.
 */
export function creditRolesKey() {
  return key('content', 'configurations', CREDIT_ROLES_KEY);
}

/** One row of `GET /admin/configurations`, narrowed to what this read needs. */
interface ConfigurationRow {
  key: string;
  value: string;
}

export function useCreditRoles() {
  return useQuery({
    queryKey: creditRolesKey(),
    queryFn: async (): Promise<readonly string[]> => {
      try {
        const rows = asRows<ConfigurationRow>(await api.get('content', '/admin/configurations'));
        const served = rows
          .find((candidate) => candidate.key === CREDIT_ROLES_KEY)
          ?.value.split(',')
          .map((token) => token.trim())
          .filter(Boolean);

        return served && served.length > 0 ? served : CREDIT_ROLES;
      } catch (error) {
        // A `403` is the operator's own role answering, not a fault. Anything
        // else is a real failure and is allowed to surface.
        if (error instanceof ApiError && error.status === 403) return CREDIT_ROLES;
        throw error;
      }
    },
    // A vocabulary changes about once a quarter.
    staleTime: STALE_TIME.vocabulary,
  });
}

// ---- the upload contract ---------------------------------------------------

export function contentTypeSchemaKey() {
  return key('content', 'contentTypes');
}

/** One declared metadata field, as the owning service publishes it. */
export interface ContentTypeField {
  name: string;
  kind: string;
  required: boolean;
  vocabulary: string[];
  maxLength: number;
}

/**
 * The metadata schema each content type declares.
 *
 * **Read, never held.** Two of the fields are closed sets — `age_rating` is a
 * vocabulary and `producing_country` an ISO alpha-2 code — and a console that
 * hard-coded either would drift from the code that enforces it, silently, until a
 * save was refused with a message that could not name what was wrong.
 */
export function useContentTypeSchema() {
  return useQuery({
    queryKey: contentTypeSchemaKey(),
    queryFn: () => api.get<{ contentType: string; fields: ContentTypeField[] }[]>('content', '/contentTypes'),
    staleTime: STALE_TIME.vocabulary,
  });
}

/** The permitted values of one declared field, or `[]` when it is not a closed set. */
export function vocabularyOf(
  schema: { contentType: string; fields: ContentTypeField[] }[] | undefined,
  contentType: string,
  fieldName: string,
): string[] {
  const entry = schema?.find((item) => item.contentType === contentType);
  return entry?.fields.find((field) => field.name === fieldName)?.vocabulary ?? [];
}

export function uploadPolicyKey() {
  return key('assets', 'uploadPolicy');
}

/**
 * The published upload contract — accepted formats, size caps, artwork purposes
 * with their ratios and minimum geometry, and the configured ladder.
 *
 * This endpoint exists **precisely so** none of those is a literal in a
 * frontend, which is why every upload zone on these surfaces prints its
 * accepted formats from here rather than from a constant beside it.
 */
const BYTES_PER_MB = 1024 * 1024;

/** The subject type a title's own artwork is authored under. */
const TITLE_SUBJECT_TYPE = 'CONTENT_ITEM';

/**
 * The payload as the assets service actually publishes it.
 *
 * Written out rather than assumed, because the two shapes disagreed on every
 * field: this console asked for `accepted_video_formats` and a byte cap, and the
 * service answers `acceptedUploadFormats` and a figure in megabytes. Nothing
 * threw — the reads were simply `undefined` — so the upload form drew «الصيغ
 * المقبولة:» with nothing after it and an artwork table with no rows, on a
 * response that had arrived intact.
 */
interface WireUploadPolicy {
  acceptedUploadFormats: string[];
  acceptedArtworkFormats: string[];
  acceptedDocumentFormats: string[];
  maxUploadMb: number;
  maxArtworkUploadMb: number;
  maxDocumentUploadMb: number;
  resolutionLadder: number[];
  artwork: Record<string, { purposes: Record<string, string>; ratios: Record<string, string> }>;
  minDimensionsByRatio: Record<string, number[]>;
  resumableUpload?: { version: string; extensions: string[]; chunkSizeBytes: number };
}

/**
 * The published upload contract — accepted formats, size caps, artwork purposes
 * with their ratios and minimum geometry, the configured ladder, and how a video
 * is transferred.
 *
 * This endpoint exists **precisely so** none of those is a literal in a
 * frontend, which is why every upload zone on these surfaces prints its
 * accepted formats from here rather than from a constant beside it.
 *
 * The payload is normalised once, here, into the shape the surfaces read.
 * Teaching every zone and every table the wire spelling instead would put the
 * same translation in eight places, and the eighth is the one that would be
 * missed. Caps become bytes because that is what a `File.size` is compared
 * against; keeping megabytes would put the multiplication at every comparison.
 */
export function useUploadPolicy() {
  return useQuery({
    queryKey: uploadPolicyKey(),
    queryFn: async (): Promise<UploadPolicy> =>
      normalizeUploadPolicy(await api.get<WireUploadPolicy>('assets', '/uploadPolicy')),
    staleTime: STALE_TIME.vocabulary,
  });
}

/**
 * One subject type's purposes, folded out of the two maps the service publishes.
 *
 * The requirement and the ratio arrive as two maps over the same keys, and the
 * minimum geometry as a third keyed by RATIO — so the join happens once, here,
 * rather than at every zone that wants to state its own floor.
 */
function normalizeArtworkPurposes(
  block: { purposes: Record<string, string>; ratios: Record<string, string> } | undefined,
  minDimensionsByRatio: Record<string, number[]> | undefined,
): ArtworkPurposePolicy[] {
  return Object.entries(block?.purposes ?? {}).map(([purpose, requirement]) => {
    const ratio = block?.ratios?.[purpose] ?? '';
    const minimum = minDimensionsByRatio?.[ratio];
    return {
      // Upper-cased because that is how this console keys its own purpose
      // labels, and the service publishes them lower-case. Left as they
      // arrive, the artwork table would print `thumbnail` in Latin script in
      // the middle of an Arabic page — a label falling through to its key.
      purpose: purpose.toUpperCase(),
      ratio,
      required: requirement === 'required',
      min_width: minimum?.[0] ?? null,
      min_height: minimum?.[1] ?? null,
    };
  });
}

function normalizeUploadPolicy(wire: WireUploadPolicy): UploadPolicy {
  const artworkBySubject = Object.fromEntries(
    Object.entries(wire.artwork ?? {}).map(([subjectType, block]) => [
      subjectType,
      normalizeArtworkPurposes(block, wire.minDimensionsByRatio),
    ]),
  );

  return {
    accepted_video_formats: wire.acceptedUploadFormats ?? [],
    accepted_artwork_formats: wire.acceptedArtworkFormats ?? [],
    accepted_document_formats: wire.acceptedDocumentFormats ?? [],
    max_video_size_bytes: megabytesToBytes(wire.maxUploadMb),
    max_artwork_size_bytes: megabytesToBytes(wire.maxArtworkUploadMb),
    max_document_size_bytes: megabytesToBytes(wire.maxDocumentUploadMb),
    resolution_ladder: wire.resolutionLadder ?? [],
    artwork_by_subject: artworkBySubject,
    artwork_purposes: artworkBySubject[TITLE_SUBJECT_TYPE] ?? [],
    resumable_upload: wire.resumableUpload
      ? {
          version: wire.resumableUpload.version,
          extensions: wire.resumableUpload.extensions,
          chunk_size_bytes: wire.resumableUpload.chunkSizeBytes,
        }
      : null,
  };
}

function megabytesToBytes(megabytes: number | undefined): number | null {
  return typeof megabytes === 'number' ? megabytes * BYTES_PER_MB : null;
}

/** The `accept` attribute for a file input, built from a published format list. */
export function toAcceptAttribute(formats: readonly string[] | undefined): string {
  return (formats ?? []).join(',');
}

/**
 * The shape and the pixel floor a cover must clear, stated as the operator reads them.
 *
 * **BOTH numbers, never the width alone.** `backend-assets` refuses a source
 * that misses the minimum on either axis of any *required* ratio, and
 * `CONTENT_ITEM.thumbnail` is `2:3` at `1000×1500`. A hint that published only
 * `min_width` told an operator holding a 1920×1080 still that `1000` was the
 * bar — the file clears it on width by nearly double and is refused on height,
 * so the console's own hint certified a file the service could never accept.
 *
 * **The ratio is READ, never written.** `2:3` is an operator's setting on
 * `ARTWORK_PURPOSES`, published on `/uploadPolicy`; a literal in the copy is
 * the defect that endpoint exists to prevent.
 *
 * Returns `null` when the policy has not loaded or names no required purpose —
 * the caller then states the formats alone rather than a floor of `0×0`.
 */
export function requiredArtworkFloor(
  policy: UploadPolicy | undefined,
): { ratio: string; minWidth: number; minHeight: number } | null {
  const purpose = (policy?.artwork_purposes ?? []).find((row) => row.required);
  if (!purpose || purpose.min_width === null || purpose.min_height === null) return null;
  return { ratio: purpose.ratio, minWidth: purpose.min_width, minHeight: purpose.min_height };
}

// ---- artwork purposes on an existing title ---------------------------------

export function itemArtworksKey(contentItemId: string) {
  return key('assets', 'artworks', 'CONTENT_ITEM', contentItemId);
}

/** One purpose of a subject's artwork, exactly as `backend-assets` states it. */
interface WireArtworkRow {
  purpose: string;
  aspectRatio: string | null;
  width: number | null;
  height: number | null;
  url: string;
}

/**
 * Which artwork purposes this title actually holds.
 *
 * `content`'s detail payload carries one cover URL and no purpose list at all —
 * `mapDetail` states `artwork_purposes: []` and says why: the composition is
 * `assets`' and this payload is not it. The authoring page read that empty list
 * straight into the purpose table, so the table drew its header and nothing
 * else on every existing title, while the same table on `/content/new` drew
 * four rows from the upload policy. An operator arriving at a saved title was
 * told the four purposes did not exist.
 *
 * The vocabulary still comes from the upload policy — the purposes and their
 * ratios are published, never compiled in — and this read supplies the only
 * thing the policy cannot know: which of them this subject has.
 */
/** The artwork a SEASON or an EPISODE holds, keyed the way `assets` keys it. */
export function subjectArtworksKey(subjectType: string, subjectId: string) {
  return key('assets', 'artworks', subjectType, subjectId);
}

/**
 * One subject's artwork rows, with their URLs.
 *
 * Distinct from `useItemArtworks` above, which answers WHICH purposes a title
 * holds and deliberately throws the URLs away — the purpose table only needs
 * presence. A season or an episode has one purpose and the whole point of
 * reading it is to draw it, so this one keeps the row.
 *
 * `subjectType` is the owning literal in SCREAMING_SNAKE; the camelCase
 * spelling is refused `422` on every call.
 */
export function useSubjectArtworks(subjectType: string, subjectId: string, enabled = true) {
  return useQuery({
    queryKey: subjectArtworksKey(subjectType, subjectId),
    queryFn: async (): Promise<readonly WireArtworkRow[]> =>
      asRows<WireArtworkRow>(
        await api.get(
          'assets',
          `/artworks?subjectType=${subjectType}&subjectId=${encodeURIComponent(subjectId)}`,
        ),
      ),
    enabled: enabled && subjectId.length > 0,
    staleTime: STALE_TIME.detail,
  });
}

export function useItemArtworks(contentItemId: string | undefined) {
  return useQuery({
    queryKey: itemArtworksKey(contentItemId ?? ''),
    queryFn: async (): Promise<readonly string[]> =>
      asRows<WireArtworkRow>(
        await api.get('assets', `/artworks?subjectType=CONTENT_ITEM&subjectId=${contentItemId}`),
        // Upper-cased at the seam, for the same reason `normalizeUploadPolicy`
        // does it: the service publishes purposes lower-case and this console
        // keys its own labels upper-case, so a set built from the raw values
        // would never match the policy's and every purpose would read «مفقودة».
      ).map((row) => row.purpose.toUpperCase()),
    enabled: Boolean(contentItemId),
    staleTime: STALE_TIME.detail,
  });
}
